import "@xyflow/react/dist/style.css";
import { Alert, Center, Loader } from "@mantine/core";
import {
    Background,
    BackgroundVariant,
    Controls,
    type Node,
    type NodeMouseHandler,
    ReactFlow,
    ReactFlowProvider,
    useReactFlow,
} from "@xyflow/react";
import { useCallback, useEffect } from "react";
import { BucketNode } from "./BucketNode";
import { useOverviewGraph } from "./hooks/useOverviewGraph";
import { type ExpandGraphProps, type ExplodeNodeData, NodeKind, NodeRole } from "./types";

const nodeTypes = { [NodeKind.BUCKET]: BucketNode };
const defaultEdgeOptions = { type: "default", style: { stroke: "#c0b6e8", strokeWidth: 1.5 } };
const proOptions = { hideAttribution: true };
const fitViewOptions = { padding: 0.1, maxZoom: 1 };
const fullFitOptions = { padding: 0.12, maxZoom: 1, duration: 500 };
const focusFitOptions = { padding: 0.22, maxZoom: 1.75, duration: 500 };

const FitOnChange = ({ signature, focusIds }: { signature: string; focusIds: string }) => {
    const { fitView, getNode } = useReactFlow();
    // biome-ignore lint/correctness/useExhaustiveDependencies: re-fit (animated) whenever the visible/focused node set changes
    useEffect(() => {
        const ids = focusIds ? focusIds.split(",") : [];
        const isMeasured = (id: string) => Boolean(getNode(id)?.measured?.width);
        let frame = 0;
        let tries = 0;
        const attempt = () => {
            tries += 1;
            if (ids.length && !ids.every(isMeasured) && tries < 40) {
                frame = requestAnimationFrame(attempt);
                return;
            }
            fitView(
                ids.length
                    ? { ...focusFitOptions, nodes: ids.map((id) => ({ id })) }
                    : fullFitOptions,
            );
        };
        frame = requestAnimationFrame(attempt);
        return () => cancelAnimationFrame(frame);
    }, [signature, focusIds, fitView, getNode]);
    return null;
};

const Canvas = ({
    index,
    rootLabel,
    category,
    subCategory,
    expanded,
    onToggleExpand,
    onOpen,
}: ExpandGraphProps) => {
    const query = useOverviewGraph({ index, rootLabel, category, subCategory, expanded });
    const flow = query.data ?? { nodes: [], edges: [] };

    const handleClick = useCallback<NodeMouseHandler>(
        (_, node) => {
            const { role, selection } = (node as Node<ExplodeNodeData>).data;
            if (role !== NodeRole.GROUP) return;
            if (selection.length === 1 && subCategory) {
                onToggleExpand(selection[0].value);
                return;
            }
            onOpen(selection);
        },
        [onToggleExpand, onOpen, subCategory],
    );

    const handlePaneClick = useCallback(() => {
        if (expanded) onToggleExpand(expanded);
    }, [expanded, onToggleExpand]);

    if (query.isError) return <Alert color="red">Failed to load facets for this view.</Alert>;
    if (!category) return <Center h={240}>Pick a category to visualize.</Center>;
    if (query.isLoading)
        return (
            <Center h={240}>
                <Loader color="purple" />
            </Center>
        );

    return (
        <div
            style={{
                height: "calc(100vh - 250px)",
                width: "100%",
                border: "1px solid #dee2e6",
                borderRadius: 8,
            }}
        >
            <ReactFlow
                nodes={flow.nodes}
                edges={flow.edges}
                nodeTypes={nodeTypes}
                defaultEdgeOptions={defaultEdgeOptions}
                proOptions={proOptions}
                onNodeClick={handleClick}
                onPaneClick={handlePaneClick}
                nodesDraggable={false}
                fitView
                fitViewOptions={fitViewOptions}
                minZoom={0.2}
                maxZoom={2}
            >
                <FitOnChange
                    signature={flow.nodes.map((n) => n.id).join(",")}
                    focusIds={
                        flow.nodes.some((n) => n.data.dimmed)
                            ? flow.nodes
                                  .filter((n) => !n.data.dimmed)
                                  .map((n) => n.id)
                                  .join(",")
                            : ""
                    }
                />
                <Background variant={BackgroundVariant.Dots} gap={24} color="#e9ecef" />
                <Controls showInteractive={false} />
            </ReactFlow>
        </div>
    );
};

export const ExpandGraph = (props: ExpandGraphProps) => (
    <ReactFlowProvider>
        <Canvas {...props} />
    </ReactFlowProvider>
);
